import { FC } from 'react';
import { Typography } from '@mui/material';

import { CardContent, CardHeader } from './Card';
import CollapsibleCard, { CollapsibleCardProps } from '../../../../../components/Card/CollapsibleCard/CollapsibleCard';
import CollapsibleCardExpandArea from '../../../../../components/Card/CollapsibleCard/CollapsibleCardExpandArea';
import CollapsibleCardExpandButton from '../../../../../components/Card/CollapsibleCard/CollapsibleCardExpandButton';
import EmptyCardContent from '../../../../../components/Card/EmptyCardContent';
import Pacing from '../../../../../components/Pacing';
import Row from '../../../../../components/Row';
import Utils from '../../../../../components/Utils';

import { Campaign, CampaignFlight, CampaignPacing } from '../../../../../types/Campaign';
import CampaignPacingUtils from '../../../../../utils/CampaignPacingUtils';

export const CampaignPacingLabels: Record<CampaignPacing, string> = {
    [CampaignPacing.UNDERPACING]: 'Underpacing',
    [CampaignPacing.SLIGHTLY_BEHIND_SCHEDULE]: 'Slightly Behind Schedule',
    [CampaignPacing.ON_TRACK]: 'On Track',
    [CampaignPacing.OVERPACING]: 'Overpacing',
};

interface CampaignPacingCardProps {
    campaign: Campaign;
    slotProps?: {
        collapsibleCard?: Partial<CollapsibleCardProps>;
    };
}

const CampaignPacingCard: FC<CampaignPacingCardProps> = ({ campaign, slotProps = {} }) => {
    const campaignFlight: CampaignFlight | null = campaign?.flights[0] ?? null;
    const hasPacing: boolean = !!campaignFlight?.budgetAmount;

    return (
        <CollapsibleCard
            defaultExpanded
            {...slotProps?.collapsibleCard}
            sx={{
                '& .MuiCardContent-root:last-child': {
                    pb: (theme) => theme.spacing(3),
                },
            }}
        >
            <CardHeader
                title={
                    <Row alignItems="center" gap={1}>
                        <Typography variant="body1">Pacing</Typography>
                    </Row>
                }
                action={<CollapsibleCardExpandButton />}
            />
            <CollapsibleCardExpandArea>
                <CardContent>
                    {hasPacing ? (
                        <Pacing
                            name="Flight Budget"
                            actual={campaignFlight.totalCost}
                            target={campaignFlight.budgetAmount}
                            percentage={campaign?.budgetPacing ? Utils.round(campaign.budgetPacing * 100, 0) : 0}
                            unit="$"
                            getColor={(actual: number, target: number, percentage: number) => {
                                return CampaignPacingUtils.getPacingColor(percentage);
                            }}
                            getLabel={(actual: number, target: number, percentage: number): string => {
                                return CampaignPacingLabels[CampaignPacingUtils.identifyPacing(percentage)];
                            }}
                        />
                    ) : (
                        <EmptyCardContent height={125} message="No pacing available" />
                    )}
                </CardContent>
            </CollapsibleCardExpandArea>
        </CollapsibleCard>
    );
};

export default CampaignPacingCard;
